<script lang="ts">
  import type {
    PostSearchResult,
    ThreadSearchResult,
  } from "@outverse-video/outverse-api-client-lib";
  import { communitySlug } from "$stores/community";
  import { threadURL, replyUrl } from "$utils/urls";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import CreationTimeInterval from "$components/+blocks/Time/TimeAgo.svelte";
  import { slugify } from "$utils/strings";
  import { Pencil, ReplyingTo, Squircle } from "$components/+blocks/OVIcon";
  import { isDarkTheme } from "app/stores/userPreferences";

  export let activity: PostSearchResult | ThreadSearchResult;
  export let memberFocus: boolean = true;

  const isThreadSearchResult = (
    activity: PostSearchResult | ThreadSearchResult
  ): activity is ThreadSearchResult => "title" in activity;

  const isPostSearchResult = (
    activity: PostSearchResult | ThreadSearchResult
  ): activity is PostSearchResult => "thread" in activity;

  const handleClick = () => {
    if (!$communitySlug) return;
    if (isThreadSearchResult(activity)) {
      return threadURL({
        communitySlug: $communitySlug,
        spaceUuid: activity.forumSpace.uuid,
        spaceSlug: slugify(activity.forumSpace.title),
        threadUuid: activity.uuid,
        threadSlug: slugify(activity.title),
      });
    }
    if (isPostSearchResult(activity)) {
      return replyUrl({
        communitySlug: $communitySlug,
        spaceUuid: activity.thread.forumSpace.uuid,
        spaceSlug: slugify(activity.thread.forumSpace.title),
        threadUuid: activity.thread.uuid,
        threadSlug: slugify(activity.thread.title),
        replySeqNum: activity.seqNum,
      });
    }
  };

  const activityOwner =
    isPostSearchResult(activity) && activity.replyToPost
      ? activity.replyToPost.author
      : activity.author;
</script>

<a href={handleClick()}>
  <div
    class="flex w-full flex-col gap-2 p-4 text-xs transition-colors duration-100 hover:cursor-pointer hover:bg-gray-400/10 dark:hover:bg-white/5"
  >
    {#if isThreadSearchResult(activity)}
      <div class="flex gap-1 text-gray-500/90 dark:text-gray-400">
        <OVIcon icon={Pencil} />
        <span class="font-medium">{memberFocus ? "Created a new thread" : "New thread"}</span>
      </div>
      <div class="flex gap-2">
        {#if memberFocus}
          <Avatar
            radius="rounded"
            mainImage={activity.author.profileImage}
            label={activity.author.handle[0]}
            backgroundColor={activity.author.profileColor}
          />
        {/if}
        <span class="truncate text-gray-600 dark:text-gray-200">
          {activity.title}
        </span>
      </div>
      <div class="inline-flex items-center gap-2 text-gray-500/90 dark:text-gray-400">
        {#if memberFocus}
          {@const forumSpace = activity.forumSpace}
          {#if forumSpace.icon}
            {@const icon =
              $isDarkTheme && forumSpace.iconDark ? forumSpace.iconDark : forumSpace.icon}
            <Avatar mainImage={icon} radius="rounded-[3px]" />
          {:else}
            <span style="color: {forumSpace.color}">
              <OVIcon icon={Squircle} class="shrink-0" />
            </span>
          {/if}
          {forumSpace.title}
          <span class="ml-auto">
            <CreationTimeInterval
              fontClasses=""
              time={activity.created}
              modified={new Date().toISOString()}
            />
          </span>
        {:else}
          <Avatar
            radius={"rounded"}
            mainImage={activityOwner.profileImage}
            label={activityOwner.handle[0]}
            backgroundColor={activityOwner.profileColor}
          />
          {activityOwner.handle}
          <span class="ml-auto">
            <CreationTimeInterval
              fontClasses=""
              time={activity.created}
              modified={new Date().toISOString()}
            />
          </span>
        {/if}
      </div>
    {/if}

    {#if isPostSearchResult(activity)}
      <div class="flex items-center gap-1 text-gray-500/90 dark:text-gray-400">
        <OVIcon icon={ReplyingTo} />
        Replied to
        {#if activity.replyToPost}
          <Avatar
            mainImage={activity.replyToPost.author.profileImage}
            label={activity.replyToPost.author.handle[0]}
            backgroundColor={activity.replyToPost.author.profileColor}
            radius={"rounded"}
          />
          <span class="mr-1 flex-1 truncate text-gray-600 dark:text-gray-100">
            {activity.replyToPost.author.handle}</span
          >
        {:else}
          <Avatar
            mainImage={activity.thread.author.profileImage}
            label={activity.thread.author.handle[0]}
            backgroundColor={activity.thread.author.profileColor}
            radius={"rounded"}
          />
          <span class="mr-1 flex-1 truncate text-gray-600 dark:text-gray-100">
            {activity.thread.author.handle}
          </span>
        {/if}
      </div>

      <div class="flex gap-2">
        {#if memberFocus}
          <Avatar
            mainImage={activity.thread.author.profileImage}
            label={activity.thread.author.handle[0]}
            backgroundColor={activity.thread.author.profileColor}
            radius={"rounded"}
          />
        {/if}
        <span class="truncate text-gray-600 dark:text-gray-200">
          {activity.thread.title}
        </span>
      </div>
      <div class="inline-flex items-center gap-2 text-gray-500/90 dark:text-gray-400">
        {#if memberFocus}
          {@const forumSpace = activity.thread.forumSpace}
          {#if forumSpace.icon}
            {@const icon =
              $isDarkTheme && forumSpace.iconDark ? forumSpace.iconDark : forumSpace.icon}
            <Avatar mainImage={icon} radius="rounded-[3px]" />
          {:else}
            <span style="color: {forumSpace.color}">
              <OVIcon icon={Squircle} class="shrink-0" />
            </span>
          {/if}
          {forumSpace.title}
          <span class="ml-auto">
            <CreationTimeInterval
              fontClasses=""
              time={activity.created}
              modified={new Date().toISOString()}
            />
          </span>
        {:else}
          <Avatar
            mainImage={activityOwner.profileImage}
            label={activityOwner.handle[0]}
            backgroundColor={activityOwner.profileColor}
            radius="rounded"
          />

          {activityOwner.handle}
          <span class="ml-auto">
            <CreationTimeInterval
              fontClasses=""
              time={activity.created}
              modified={new Date().toISOString()}
            />
          </span>
        {/if}
      </div>
    {/if}
  </div>
</a>
